import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { apiClient } from '@/lib/api/client'
import { activityFeature, activitySession } from './activitySession'

export function useArtistActivity() {
  const { pathname } = useLocation()
  const user = useAuthStore(state => state.user)
  const masquerade = useAuthStore(state => state.masquerade)
  const userId = user?.id
  const role = user?.role

  useEffect(() => {
    const feature = activityFeature(pathname)
    if (!userId || role !== 'ARTIST' || masquerade || !feature || navigator.doNotTrack === '1') return
    let lastInteraction = Date.now()
    let lastSent = 0
    let inFlight = false
    const observe = () => {
      const now = Date.now()
      if (document.visibilityState !== 'visible' || now - lastInteraction > 5 * 60 * 1000 ||
        now - lastSent < 60 * 1000 || inFlight) return
      try {
        const sessionId = activitySession(sessionStorage, userId, now)
        inFlight = true
        lastSent = now
        void apiClient.post('/api/activity', { feature, sessionId }).catch(() => {
          // Retry on the next observation; telemetry never interrupts the artist.
          lastSent = 0
        }).finally(() => { inFlight = false })
      } catch { /* Storage can be unavailable in restricted browsing modes. */ }
    }
    const interact = () => { lastInteraction = Date.now(); observe() }
    const visible = () => { if (document.visibilityState === 'visible') interact() }
    observe()
    const interval = window.setInterval(observe, 60 * 1000)
    window.addEventListener('pointerdown', interact)
    window.addEventListener('keydown', interact)
    window.addEventListener('scroll', interact, true)
    document.addEventListener('visibilitychange', visible)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('pointerdown', interact)
      window.removeEventListener('keydown', interact)
      window.removeEventListener('scroll', interact, true)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [pathname, userId, role, masquerade])
}
